import React, { useMemo } from 'react';
import { COLUMN_TYPES } from '../../../../config/app.config';
import { BROADCAST_TYPES } from '../../../../constants/constants';
import { useNestedModal } from '../../../modals/AppointmentStatusesArchive/AppointmentStatusesArchive.hooks';
import { DataTable } from '../../../modals/ModalTables';
import {
  ImageGalleryModal,
  TextDisplayModal,
} from '../../../modals/ContentModals';
import ModalPdfViewer from '../../Common/ModalPdfViewer';

const audienceMap = BROADCAST_TYPES.reduce((acc, item) => {
  acc[item.id] = item.name;
  return acc;
}, {});

function getAudienceLabel(ids = []) {
  if (!ids || ids.length === 0) return '-';
  if (ids.includes('all_users') || ids.length === BROADCAST_TYPES.length) {
    return 'All Users';
  }
  const names = ids.map((id) => audienceMap[id] || id);
  if (names.length === 1) return names[0];
  return `${names[0]} + ${names.length - 1} more`;
}

function getAudienceDetail(ids = []) {
  if (!ids || ids.length === 0) return '';
  if (ids.includes('all_users') || ids.length === BROADCAST_TYPES.length) {
    return BROADCAST_TYPES.filter((t) => t.id !== 'all_users')
      .map((t) => t.name)
      .join('\n');
  }
  return ids.map((id) => audienceMap[id] || id).join('\n');
}

function getFacilityLabel(facilityNames = [], totalFacilityCount = 0) {
  if (!facilityNames || facilityNames.length === 0) return '-';
  if (totalFacilityCount > 0 && facilityNames.length >= totalFacilityCount) {
    return 'All Facilities';
  }
  if (facilityNames.length === 1) return facilityNames[0];
  return `${facilityNames[0]} + ${facilityNames.length - 1} more`;
}

function getFacilityDetail(facilityNames = []) {
  if (!facilityNames || facilityNames.length === 0) return '';
  return facilityNames.join('\n');
}

export function BroadcastArchiveTable({
  broadcasts = [],
  isSuperAdmin = false,
  totalFacilityCount = 0,
  onCreateClick,
}) {
  const messageModal = useNestedModal();
  const imagesModal = useNestedModal();
  const pdfModal = useNestedModal();
  const audienceModal = useNestedModal();
  const facilityModal = useNestedModal();

  const tableColumns = useMemo(() => {
    const columns = [
      {
        header: 'Date',
        field: 'createdAt',
        type: COLUMN_TYPES.DATE,
        headerClassName: 'date-column',
      },
      {
        header: 'Time',
        field: 'createdAt',
        type: COLUMN_TYPES.TIME,
        headerClassName: 'time-column',
      },
      {
        header: 'Images',
        field: 'images',
        type: COLUMN_TYPES.IMAGES,
        headerClassName: 'broadcast-images-column',
        onImageClick: (rowData) => imagesModal.open(rowData.images),
        buttonText: (count) => `View (${count})`,
      },
{
  header: 'PDFs',
  field: 'documents',
  type: COLUMN_TYPES.PDFS,
  headerClassName: 'broadcast-pdfs-column',
  onPdfClick: (rowData) => pdfModal.open(rowData.documents),
  buttonText: (count) => `View (${count})`,
},
...(isSuperAdmin
  ? [
      {
        header: 'Push',
        field: 'sendPush',
        type: COLUMN_TYPES.CUSTOM,
        headerClassName: 'broadcast-push-column',
        renderCell: (cellValue) => {
          if (cellValue === true) return 'Yes';
          if (cellValue === false) return 'No';
          return '-';
        },
      },
    ]
  : []),
{
  header: 'Subject',
  field: 'subject',
  type: COLUMN_TYPES.TEXT,
  headerClassName: 'broadcast-subject-column',
},
      {
        header: 'Message',
        field: 'description',
        type: COLUMN_TYPES.TRUNCATED_TEXT,
        headerClassName: 'broadcast-message-column',
        maxLines: 3,
        ellipsisRenderer: (message, rowData) => (
          <span>
            ...{' '}
            <button
              type="button"
              className="nav-link-style"
              onClick={() => messageModal.open(rowData)}
            >
              View
            </button>
          </span>
        ),
      },
      {
        header: 'Audience',
        field: 'broadcastAudience',
        type: COLUMN_TYPES.CUSTOM,
        headerClassName: 'broadcast-audience-column',
        renderCell: (cellValue) => {
          const label = getAudienceLabel(cellValue);
          if (label === '-') return '-';
          return (
            <button
              type="button"
              className="nav-link-style"
              onClick={() =>
                audienceModal.open({
                  title: 'Audience',
                  detail: getAudienceDetail(cellValue),
                })
              }
            >
              {label}
            </button>
          );
        },
      },
    ];

    if (isSuperAdmin) {
      columns.push({
        header: 'Facility',
        field: 'facilityNames',
        type: COLUMN_TYPES.CUSTOM,
        headerClassName: 'broadcast-facility-column',
        renderCell: (cellValue) => {
          const label = getFacilityLabel(cellValue, totalFacilityCount);
          if (label === '-') return '-';
          return (
            <button
              type="button"
              className="nav-link-style"
              onClick={() =>
                facilityModal.open({
                  title: 'Facilities',
                  detail: getFacilityDetail(cellValue),
                })
              }
            >
              {label}
            </button>
          );
        },
      });
    }

    return columns;
  }, [isSuperAdmin, totalFacilityCount, messageModal, imagesModal, pdfModal, audienceModal, facilityModal]);

  const rows = useMemo(
    () =>
      broadcasts.map((b) => ({
        ...b,
        facilityNames: b.broadcastFacilities?.length > 0
          ? b.broadcastFacilities.map((f) => f.displayName)
          : b.facility?.displayName
            ? [b.facility.displayName]
            : [],
      })),
    [broadcasts],
  );

  return (
    <>
      <div className="broadcast-archive-table">
        <div className="broadcast-archive-header">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onCreateClick}
          >
            Create Broadcast
          </button>
        </div>
        <div className="broadcast-archive-content">
          <DataTable
            columns={tableColumns}
            rows={rows}
            noDataMessage="No broadcasts sent yet..."
            noDataColSpan={isSuperAdmin ? 9 : 7}
          />
        </div>
      </div>

      <TextDisplayModal
        modalClassName="centered"
        isOpen={messageModal.isOpen}
        onClose={messageModal.close}
        title="Broadcast Message"
        content={messageModal?.record?.description || ''}
      />

      <TextDisplayModal
        modalClassName="centered"
        isOpen={audienceModal.isOpen}
        onClose={audienceModal.close}
        title={audienceModal?.record?.title || 'Audience'}
        content={audienceModal?.record?.detail || ''}
      />

      <TextDisplayModal
        modalClassName="centered"
        isOpen={facilityModal.isOpen}
        onClose={facilityModal.close}
        title={facilityModal?.record?.title || 'Facilities'}
        content={facilityModal?.record?.detail || ''}
      />

      <ImageGalleryModal
        modalClassName="centered"
        isOpen={imagesModal.isOpen}
        onClose={imagesModal.close}
        title="Broadcast Images"
        images={imagesModal?.record || []}
      />

      <ModalPdfViewer
        isOpen={pdfModal.isOpen}
        dataPdf={pdfModal?.record || []}
        onClose={pdfModal.close}
      />
    </>
  );
}
